// The duty form (views/duty/duty-edit.js). A new duty starts at 12 hours, rest break off, 10 minutes and no
// supervisor; editing a duty starts from what it has (duty/get gives duties.*: duration in minutes,
// rest_break, rest_time, rest_meter, supervisor_id), so a Save that changes only the time keeps the rest.

export const NEW_DUTY = {
	selectedDuration: { value: 12, label: '12 hours' },
	rest_break: false,
	selectedMinute: { value: 10, label: '10' },
	selectedMeter: { value: 10, label: '10' },
	supervisorId: null,
};

function whole(value) {
	const n = Number(value);
	return value !== null && value !== '' && typeof value !== 'boolean' && Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function hoursLabel(hours) {
	return hours + (hours === 1 ? ' hour' : ' hours');
}

// The form's values for a stored duty; anything missing or unreadable falls back to the new-duty value
export function dutyFormValues(duty) {
	if (!duty || typeof duty !== 'object') {
		return { ...NEW_DUTY };
	}
	const minutes = whole(duty.duration);
	// A duration that is not whole hours is shown as it is, in hours (e.g. 7.5 hours), and saved unchanged
	const hours = minutes === null ? null : minutes / 60;
	const restTime = whole(duty.rest_time);
	const restMeter = whole(duty.rest_meter);
	const supervisor = whole(duty.supervisor_id);
	return {
		selectedDuration: hours === null ? NEW_DUTY.selectedDuration : { value: hours, label: hoursLabel(hours) },
		rest_break: duty.rest_break === true || duty.rest_break === 1 || duty.rest_break === '1' || duty.rest_break === 'true',
		selectedMinute: restTime === null ? NEW_DUTY.selectedMinute : { value: restTime, label: String(restTime) },
		selectedMeter: restMeter === null ? NEW_DUTY.selectedMeter : { value: restMeter, label: String(restMeter) },
		supervisorId: supervisor,
	};
}

// A start time that has already passed is saved only after "Save anyway" (no time-zone note: the picked time is
// the location's clock, as users expect)
export const PAST_START_MESSAGE = "This duty's start time has already passed. Alerts before now will be recorded as missed. Save anyway?";

export function startHasPassed(startMs, nowMs = Date.now()) {
	const start = Number(startMs);
	return startMs !== null && startMs !== undefined && startMs !== '' && Number.isFinite(start) && start < nowMs;
}

// Saving a new duty for a guard replaces all their current and future duties (laravel duty/register): said on the
// duty form and on the guard's duties page, so nobody deletes old duties one by one first
export const REPLACES_DUTIES_NOTE = "Creating a new duty for this guard replaces all their current and future duties, so there's no need to delete old duties first.";

// The popup before saving a new duty for a guard who has current or future duties (count from duty/replacedCount);
// null when the count could not be read
export function replacesDutiesMessage(count) {
	if (count === null || count === undefined || !Number.isFinite(Number(count))) {
		return 'This guard may already have current or future duties. Saving this new duty replaces them.';
	}
	const n = Number(count);
	return 'This guard already has ' + n + ' current or future ' + (n === 1 ? 'duty' : 'duties') + '. Saving this new duty replaces them.';
}

// What to ask before saving, or null to save straight away: one popup with every message that applies.
// replaceCount: the guard's current or future duties a new duty replaces (0 when editing a duty, or none; null: unknown).
export function saveConfirmation({ pastStart = false, replaceCount = 0 } = {}) {
	const replaces = replaceCount === null || replaceCount === undefined || Number(replaceCount) > 0;
	if (!replaces && !pastStart) {
		return null;
	}
	const messages = [];
	if (replaces) {
		messages.push(replacesDutiesMessage(replaceCount));
	}
	if (pastStart) {
		messages.push(PAST_START_MESSAGE);
	}
	// The start-time popup alone keeps its "Save anyway"
	return { messages, button: replaces ? 'Save' : 'Save anyway' };
}

// Why a duty cannot be edited (the duty page's Edit), or null when it has not started
export function noEditMessage(startMs, endMs, nowMs = Date.now()) {
	if (!(Number(startMs) <= nowMs)) {
		return null;
	}
	return Number(endMs) < nowMs
		? 'This duty has finished, so it can no longer be edited.'
		: 'This duty is running, so it can no longer be edited.';
}
